/**
 * translate.ts — Raw trading data → plain English
 * No jargon. No acronyms. Just "what does this mean for me?"
 */

import { isLiveSnapshotAgent } from "./dashboardDataContract";

export interface IntelData {
  symbol: string;
  direction?: string;
  orb_lifecycle_state?: string;
  orb_lifecycle_reason?: string;
  orb_lifecycle_recorded_at?: string;
  orb_lifecycle_direction?: string;
  orb_lifecycle_entry?: number;
  orb_lifecycle_current_price?: number;
  orb_lifecycle_score?: number;
  orb_lifecycle_trigger_type?: string;
  orb_lifecycle_terminal?: boolean;
  hmm_regime?: string;
  hmm_probability?: number;
  kronos_direction?: string;
  kronos_confidence?: number;
  kronos_daily_method?: string;
  kronos_daily_input_anchor_source?: string;
  kronos_daily_change_pct?: number;
  kronos_daily_predicted_close?: number;
  kronos_daily_predicted_high?: number;
  kronos_daily_predicted_low?: number;
  kronos_daily_range?: number;
  kronos_daily_valid?: boolean;
  kronos_daily_invalid_reason?: string;
  kronos_4h_direction?: string;
  kronos_4h_confidence?: number;
  kronos_4h_method?: string;
  kronos_4h_source?: string;
  kronos_4h_change_pct?: number;
  kronos_4h_predicted_open?: number;
  kronos_4h_predicted_close?: number;
  kronos_4h_predicted_high?: number;
  kronos_4h_predicted_low?: number;
  kronos_4h_range?: number;
  kronos_4h_input_anchor_as_of?: number;
  kronos_4h_input_anchor_price?: number;
  kronos_4h_input_anchor_source?: string;
  kronos_4h_input_anchor_age_sec?: number;
  kronos_4h_anchor_status?: string;
  kronos_4h_anchor_rebased?: boolean;
  kronos_4h_valid?: boolean;
  kronos_4h_invalid_reason?: string;
  kronos_4h_pre_rebase_predicted_open?: number;
  kronos_4h_open_anchor_gap_pct?: number;
  kronos_4h_anchor_rebase_delta?: number;
  kronos_hourly_predicted_open?: number;
  kronos_hourly_predicted_close?: number;
  kronos_hourly_predicted_high?: number;
  kronos_hourly_predicted_low?: number;
  kronos_hourly_range?: number;
  kronos_hourly_input_anchor_as_of?: number;
  kronos_hourly_input_anchor_price?: number;
  kronos_hourly_input_anchor_source?: string;
  kronos_hourly_input_anchor_age_sec?: number;
  kronos_hourly_anchor_status?: string;
  kronos_hourly_anchor_rebased?: boolean;
  kronos_hourly_valid?: boolean;
  kronos_hourly_invalid_reason?: string;
  kronos_hourly_pre_rebase_predicted_open?: number;
  kronos_hourly_open_anchor_gap_pct?: number;
  kronos_hourly_anchor_rebase_delta?: number;
  squeeze_on?: boolean;
  squeeze_fired?: boolean;
  squeeze_momentum?: number;
  kronos_hourly_direction?: string;
  kronos_hourly_confidence?: number;
  kronos_hourly_method?: string;
  hmm_4h_regime?: string;
  hmm_4h_probability?: number;
  hmm_hourly_regime?: string;
  hmm_hourly_probability?: number;
  squeeze_stage?: string;
  squeeze_pct?: number;
  squeeze_tier?: string;
  squeeze_action?: string;
  adx?: number;
  adx_label?: string;
  adx_plus_di?: number;
  adx_minus_di?: number;
  vix?: number;
  vix_regime?: string;
  tv_rating?: string;
  vix_slope?: string;
  vix_ma10?: number;
  vix_ma20?: number;
  intel_stance?: string;
  intel_alignment_score?: number;
  intel_layers_for?: string;
  intel_layers_against?: string;
  vol_ratio?: number;
  entry?: number;
  stop_loss?: number;
  tp1?: number;
  tp2?: number;
  outcome?: string;
  pnl_dollars?: number;
  created_at?: number;
  hmm_composite_regime?: string;
  hmm_composite_confidence?: number;
  squeeze_momentum_direction?: string;
  // Multi-timeframe squeeze
  squeeze_1h_on?: boolean;
  squeeze_1h_fired?: boolean;
  squeeze_1h_direction?: string;
  squeeze_1h_stage?: string;
  squeeze_1h_pct?: number;
  squeeze_1h_pro_level?: string;
  squeeze_1h_status?: string;
  squeeze_1h_status_reason?: string;
  squeeze_4h_on?: boolean;
  squeeze_4h_fired?: boolean;
  squeeze_4h_direction?: string;
  squeeze_4h_stage?: string;
  squeeze_4h_pct?: number;
  squeeze_4h_pro_level?: string;
  squeeze_4h_status?: string;
  squeeze_4h_status_reason?: string;
  squeeze_daily_on?: boolean;
  squeeze_daily_fired?: boolean;
  squeeze_daily_direction?: string;
  squeeze_daily_stage?: string;
  squeeze_daily_pct?: number;
  squeeze_daily_pro_level?: string;
  squeeze_daily_status?: string;
  squeeze_daily_status_reason?: string;
  // SMA-50 / EMA-50 alignment
  sma50_value?: number;
  sma50_aligned?: boolean | null;
  ema50_value?: number;
  ema50_aligned?: boolean | null;
  // QST-334: Volume flow indicators (LOG ONLY)
  obv_divergence_10?: string;   // "BULL_DIV" | "BEAR_DIV" | "ALIGNED"
  obv_divergence_20?: string;   // "BULL_DIV" | "BEAR_DIV" | "ALIGNED"
  cmf_20?: number;              // Chaikin Money Flow (-0.5 to +0.5)
  mfi_14?: number;              // Money Flow Index (0-100)
  fbd_flow_score?: number;      // 0.0–1.0 bullish volume composite
  mr_flow_score?: number;       // 0.0–1.0 bearish volume composite
  // QST-320: Realtime order-flow indicators
  iceberg_bid?: boolean;
  iceberg_ask?: boolean;
  absorption_bid?: boolean;
  absorption_ask?: boolean;
  institutional_direction?: string;
  institutional_score?: number;
  spoof_bid_risk?: number;
  spoof_ask_risk?: number;
  // Tape coach V2 fields. These are live-market read fields, not executable
  // ORB/FBD/MR signal levels.
  current_bias?: string;
  order_book_bias?: string;
  volume_flow_bias?: string;
  flow_state?: string;
  flow_state_bias?: string;
  flow_state_confidence?: number;
  entry_action?: string;
  entry_reason?: string;
  entry_plan?: Record<string, any>;
  entry_blockers?: string[];
  confirmation?: string;
  invalidation?: string;
  delta?: Record<string, number>;
  cumulative_tape?: Record<string, any>;
  data_warnings?: string[];
  order_flow_status?: string;
  order_flow_age_sec?: number;
  volume_flow_status?: string;
  volume_flow_age_sec?: number;
  volume_flow_as_of?: number;
  volume_flow_source?: string;
  volume_flow_candle_seconds?: number;
  volume_flow_trade_count?: number;
  volume_flow_coverage_sec?: number;
  tape_accumulation_windows?: Record<string, TapeAccumulationWindow>;
  tape_accumulation_source?: string;
  tape_accumulation_as_of?: number;
  market_flow_stack?: MarketFlowStack;
  order_flow_source?: string;
  order_flow_method?: string;
  order_flow_as_of?: number;
  date?: string;
  session?: string; // "asia" or "us" for the live forecast/intel snapshot
  session_label?: string; // Human label when supplied by backend, e.g. "Asia" or "NY/US"
  agent?: string;
  pending_session?: string; // "asia" or "us" when no signals yet for that session
  data_status?: "live" | "stale" | "missing";
  source_label?: string;
  freshness_label?: string;
  current_price?: number;
  current_price_source?: string;
  current_price_as_of?: number;
  current_price_age_sec?: number;
  current_price_status?: string;
  current_price_disagreement?: boolean;
  current_price_outlier_sources?: string | null;
  has_open_trade?: boolean;
  open_trade_agent?: string;
  open_trade_direction?: string;
  open_trade_session?: string;
  open_trade_current_session?: string;
  open_trade_is_current_session?: boolean;
  open_trade_entry?: number;
  open_trade_stop_loss?: number;
  open_trade_tp1?: number;
  open_trade_tp2?: number;
  // QST-343 / FBE-3: Forecast Belief Engine v1 fields (LOG-ONLY).
  // Populated by scripts/upload_live_dashboard_snapshot.py from
  // scripts/forecast_belief_engine.py via liveMarketSnapshots.payload.
  // These are watch-only — they MUST NOT influence trade execution,
  // sizing, gating, or alerts on the dashboard.
  forecast_raw_state?: ForecastState;
  forecast_raw_confidence?: number;            // 0–100
  forecast_raw_probs?: ForecastProbs;          // 4-bucket prob distribution
  forecast_stable_state?: ForecastState;
  forecast_stable_confidence?: number;          // 0–100
  forecast_transition_probability?: number;     // 0–1
  forecast_state_age_min?: number;              // minutes the stable state has held
  forecast_flip_suppressed?: boolean;
  forecast_stability_score?: number;            // 0–100
  forecast_reason?: string;
  forecast_engine_version?: string;
  forecast_generated_at?: number;
  forecast_generated_at_iso?: string;
  forecast_contract?: string;
  forecast_session_id?: string;
  forecast_anchor_type?: string;
  forecast_anchor_price?: number;
  forecast_anchor_preserved?: boolean;
  forecast_authoritative_channel?: string;
  forecast_candidate_channel?: string;
  forecast_release_policy?: string;
  forecast_accuracy_status?: string;
  forecast_data_integrity_status?: string;
  forecast_data_integrity_issues?: string;
  forecast_trust_status?: string;
  forecast_trading_approved?: boolean;
  forecast_calibration_sample_size?: number;
  forecast_calibration_wins?: number;
  forecast_calibration_losses?: number;
  forecast_calibration_flats?: number;
  forecast_calibration_win_rate?: number;
  forecast_calibration_avg_brier?: number;
  forecast_calibration_horizon_results?: Record<string, Record<string, number>>;
  // FBE Bias workspace only: setup-family guidance remains LOG-ONLY and is
  // intentionally excluded from Command Center field extraction.
  forecast_trader_bias?: string;
  forecast_trade_mode?: string;
  forecast_preferred_setups?: string[];
  forecast_avoid_setups?: string[];
  forecast_guidance_reason?: string;
  forecast_guidance_scope?: string;
  forecast_guidance_contract?: string;
  forecast_guidance_execution_approved?: boolean;
  candidate_forecast_raw_state?: ForecastState;
  candidate_forecast_raw_confidence?: number;
  candidate_forecast_raw_probs?: ForecastProbs;
  candidate_forecast_stable_state?: ForecastState;
  candidate_forecast_stable_confidence?: number;
  candidate_forecast_transition_probability?: number;
  candidate_forecast_state_age_min?: number;
  candidate_forecast_flip_suppressed?: boolean;
  candidate_forecast_stability_score?: number;
  candidate_forecast_reason?: string;
  candidate_forecast_engine_version?: string;
  candidate_forecast_contract?: string;
  candidate_forecast_session_id?: string;
  candidate_forecast_anchor_type?: string;
  candidate_forecast_anchor_price?: number;
  candidate_forecast_anchor_preserved?: boolean;
  candidate_forecast_session_start_ms?: number;
  candidate_forecast_session_end_ms?: number;
  candidate_forecast_calibration_sample_size?: number;
  candidate_forecast_calibration_win_rate?: number;
  candidate_forecast_calibration_avg_brier?: number;
  forecast_scope?: "session" | "pre_session" | "post_session" | string;
  forecast_phase?: "active_session" | "pre_session" | "post_session" | string;
  forecast_session_start_ms?: number;
  forecast_session_end_ms?: number;
  forecast_prior_state?: ForecastState;
  forecast_prior_confidence?: number;
  forecast_prior_created_at?: number;
  forecast_prior_created_at_iso?: string;
  forecast_prior_source?: string;
}

export interface TapeAccumulationWindow {
  window_sec?: number;
  tape_bias?: string;
  flow_state?: string;
  buy_volume?: number;
  sell_volume?: number;
  delta?: number;
  delta_ratio?: number;
  price_change?: number;
  trade_count?: number;
}

export interface MarketFlowStack {
  verdict?: "LONG_CONFIRMATION" | "SHORT_CONFIRMATION" | "LONG_DEVELOPING" | "SHORT_DEVELOPING" | "CONFLICT_WAIT" | "NO_CLEAR_EDGE" | string;
  book_side?: "long" | "short" | "neutral" | string;
  book_strength?: number;
  book_reason?: string;
  tape_5m?: "long" | "short" | "neutral" | string;
  tape_15m?: "long" | "short" | "neutral" | string;
  tape_30m?: "long" | "short" | "neutral" | string;
  flow_state_5m?: string;
  flow_state_15m?: string;
  flow_state_30m?: string;
  alignment_score?: number;
  risk_flags?: string[];
  human_summary?: string;
  source?: string;
  computed_at?: string;
}

/**
 * Forecast Belief Engine state labels (QST-341 / QST-343 LOG-ONLY).
 *
 * Mirror of the strings emitted by ``scripts/forecast_belief_engine.py``.
 * The dashboard only reads these — it never sets them.
 */
export type ForecastState =
  | "bullish"
  | "bearish"
  | "flat"
  | "mixed"
  | "unstable";

/**
 * Probability buckets emitted by the Forecast Belief Engine.
 * Each is a float in [0, 1]; the four together sum to ≈ 1.
 */
export interface ForecastProbs {
  bullish: number;
  bearish: number;
  flat: number;
  mixed_unstable: number;
}

export type VerdictAction = "BUY" | "SELL" | "WAIT" | "BULLISH" | "BEARISH" | "MIXED" | "FLAT";

export interface InstrumentVerdict {
  action: VerdictAction;
  /** forecast = AI/HMM/Kronos market read; trade = executable strategy signal; empty = missing/pending state. */
  kind: "forecast" | "trade" | "empty";
  confidence: number; // 0-100
  reason: string; // one-line plain English
  checks: CheckItem[];
  riskLevel: "LOW" | "MEDIUM" | "HIGH";
}

export interface CheckItem {
  label: string;
  status: "agree" | "disagree" | "warning" | "neutral";
  text: string;
}

// ── Direction helpers ──

// Direction classification is handled by directionLabel() below

function directionLabel(dir?: string): "up" | "down" | "flat" | "unknown" {
  if (!dir) return "unknown";
  const d = dir.toLowerCase();
  if (d.includes("up") || d.includes("bull") || d === "long") return "up";
  if (d.includes("down") || d.includes("bear") || d === "short") return "down";
  if (d.includes("flat") || d.includes("neutral") || d.includes("ranging") || d.includes("range")) return "flat";
  return "unknown";
}

// ── Individual layer translations ──
// Each takes the signal direction so it can classify as agree/warning/disagree

function translateHMM(regime: string | undefined, prob: number | undefined, signalDir: "up" | "down" | "flat" | "unknown", comparisonLabel = "your signal"): CheckItem {
  if (!regime) return { label: "Market trend", status: "neutral", text: "No trend data" };
  const r = regime.toLowerCase();
  const confidence = prob ? ` (${Math.round(prob * 100)}% sure)` : "";
  const hmmDir = directionLabel(regime);

  if (r.includes("ranging")) {
    // Ranging = no trend, warning for directional signals
    return { label: "Market trend", status: "warning", text: `Market is sideways/ranging — no clear trend${confidence}` };
  }
  if (r.includes("low_vol")) {
    return { label: "Market trend", status: "warning", text: `Very low activity${confidence}` };
  }

  // Directional HMM — compare to signal
  if (signalDir === "unknown" || hmmDir === "flat" || hmmDir === "unknown") {
    return { label: "Market trend", status: "neutral", text: regime.replace(/_/g, " ") + confidence };
  }
  if (hmmDir === signalDir) {
    return { label: "Market trend", status: "agree", text: `Market is ${regime.replace(/_/g, " ")}${confidence}` };
  }
  return { label: "Market trend", status: "warning", text: `Market is ${regime.replace(/_/g, " ")} — against ${comparisonLabel}${confidence}` };
}

function translateKronos(dir: string | undefined, conf: number | undefined, changePct: number | undefined, signalDir: "up" | "down" | "flat" | "unknown", comparisonLabel = "your signal"): CheckItem {
  if (!dir) return { label: "AI prediction", status: "neutral", text: "No AI prediction" };
  const c = conf ? `${Math.round(conf * 100)}% sure` : "";
  const pct = changePct ? ` (${changePct > 0 ? "+" : ""}${changePct.toFixed(1)}%)` : "";
  const kronosDir = directionLabel(dir);

  if (kronosDir === "flat") return { label: "AI prediction", status: "neutral", text: `AI expects sideways movement${pct} ${c}` };
  if (signalDir === "unknown" || kronosDir === "unknown") {
    return { label: "AI prediction", status: "neutral", text: `AI predicts ${dir}${pct} ${c}` };
  }
  if (kronosDir === signalDir) {
    return { label: "AI prediction", status: "agree", text: `AI predicts price going ${dir}${pct} ${c}` };
  }
  return { label: "AI prediction", status: "warning", text: `AI predicts price going ${dir} — against ${comparisonLabel}${pct} ${c}` };
}

function translateKronos4H(dir: string | undefined, conf: number | undefined, signalDir: "up" | "down" | "flat" | "unknown", comparisonLabel = "your signal"): CheckItem {
  if (!dir) return { label: "Session forecast", status: "neutral", text: "No session forecast" };
  const c = conf ? ` (${Math.round(conf * 100)}% sure)` : "";
  const kronosDir = directionLabel(dir);

  if (kronosDir === "flat") return { label: "Session forecast", status: "neutral", text: `Session looks sideways/ranging${c}` };
  if (signalDir === "unknown" || kronosDir === "unknown") {
    return { label: "Session forecast", status: "neutral", text: `Next few hours look ${dir}${c}` };
  }
  if (kronosDir === signalDir) {
    return { label: "Session forecast", status: "agree", text: `Next few hours look ${dir}${c}` };
  }
  return { label: "Session forecast", status: "warning", text: `Next few hours look ${dir} — against ${comparisonLabel}${c}` };
}

function translateADX(adx: number | undefined, label: string | undefined, plusDi: number | undefined, minusDi: number | undefined, signalDir: "up" | "down" | "flat" | "unknown", isLiveMarketRead = false): CheckItem {
  if (adx === undefined || adx === null) return { label: "Trend strength", status: "neutral", text: "No trend strength data" };
  const l = label?.toLowerCase() || "";
  // ADX measures trend STRENGTH (chop index), not direction.
  // High ADX = trending → breakouts work. Low ADX = choppy → breakouts fail.
  // +DI/-DI indicate trend direction for context text only.
  const trendDir = (plusDi !== undefined && minusDi !== undefined)
    ? (plusDi > minusDi ? "up" : "down")
    : "unknown";
  const dirText = trendDir === "up" ? "bullish" : trendDir === "down" ? "bearish" : "";

  const opposesSignal = signalDir !== "unknown" && signalDir !== "flat" && trendDir !== "unknown" && trendDir !== signalDir;
  const alignsWithSignal = signalDir !== "unknown" && signalDir !== "flat" && trendDir === signalDir;
  const directionText = dirText ? `${dirText} ` : "";

  const opposingContext = isLiveMarketRead
    ? `${signalDir === "up" ? "bullish" : "bearish"} market lean`
    : `${signalDir === "up" ? "buy" : "sell"} setup`;

  if (l.includes("strong")) {
    if (opposesSignal) {
      return { label: "Trend strength", status: "warning", text: `Very strong ${directionText}trend — against this ${opposingContext}` };
    }
    return { label: "Trend strength", status: alignsWithSignal ? "agree" : "neutral", text: `Very strong ${directionText}trend` };
  }
  if (l.includes("trend")) {
    if (opposesSignal) {
      return { label: "Trend strength", status: "warning", text: `Moderate ${directionText}trend — against this ${opposingContext}` };
    }
    return { label: "Trend strength", status: alignsWithSignal ? "agree" : "neutral", text: `Moderate ${directionText}trend` };
  }
  return { label: "Trend strength", status: "warning", text: `Weak trend — choppy, breakouts may fail` };
}

/**
 * Translate squeeze/momentum state into plain English.
 *
 * IMPORTANT: This function expects ALREADY-RESOLVED data from dataResolution.ts.
 * Momentum neutralization and threshold checks happen BEFORE this function.
 * translateSqueeze NEVER makes fallback or threshold decisions.
 */
function translateSqueeze(on: boolean | undefined, fired: boolean | undefined, momentum: number | undefined): CheckItem {
  if (fired) {
    const dir = momentum && momentum > 0 ? "bullish" : momentum && momentum < 0 ? "bearish" : "";
    const extra = dir ? ` — momentum is ${dir}` : "";
    return { label: "Momentum", status: "agree", text: `Breakout starting${extra}` };
  }
  if (on) return { label: "Momentum", status: "warning", text: "Energy building — wait for breakout" };
  // squeeze_on=false after resolution means: data present, no active squeeze.
  // If momentum was neutralized by dataResolution, it's already set to 0.
  if (on === false) {
    if (momentum !== undefined && momentum !== null && momentum !== 0) {
      const dir = momentum > 0 ? "bullish" : "bearish";
      return { label: "Momentum", status: "agree", text: `${dir} momentum (no squeeze)` };
    }
    return { label: "Momentum", status: "neutral", text: "No momentum signal" };
  }
  // squeeze_on=undefined after resolution means: truly no data
  return { label: "Momentum", status: "neutral", text: "No momentum data" };
}

function translateVIX(vix: number | undefined): CheckItem {
  if (vix === undefined || vix === null) return { label: "Fear level", status: "neutral", text: "No fear data" };
  // Thresholds documented in dataResolution.ts — kept inline here for VIX
  // because VIX has no resolution step (it's a direct value).
  if (vix >= 30) return { label: "Fear level", status: "warning", text: `High fear (VIX ${Math.round(vix)}) — expect big swings` };
  if (vix >= 25) return { label: "Fear level", status: "warning", text: `Elevated fear (VIX ${Math.round(vix)}) — volatile` };
  return { label: "Fear level", status: "agree", text: `Calm market (VIX ${Math.round(vix)})` };
}

/**
 * Translate the intel stance (from Convex live snapshot or derived) into plain English.
 *
 * IMPORTANT: Stance resolution happens in dataResolution.ts.
 * If no stance is available, dataResolution falls back to deriving one from layers.
 * translateStance NEVER decides whether to use live vs derived stance.
 */
function translateStance(stance: string | undefined, _score: number | undefined, signalDir: "up" | "down" | "flat" | "unknown", comparisonLabel = "your signal"): CheckItem {
  if (stance) {
    const s = stance.toLowerCase();

    if (s.includes("aligned_bullish") || s.includes("aligned_bearish")) {
      const alignedDir = s.includes("bullish") ? "up" : "down";
      if (signalDir !== "unknown" && alignedDir !== signalDir) {
        return { label: "Intel layers", status: "warning", text: `AI layers aligned ${alignedDir} — against ${comparisonLabel}` };
      }
      return { label: "Intel layers", status: "agree", text: "AI layers agree" };
    }
    if (s.includes("conflicted") || s.includes("mixed")) {
      return { label: "Intel layers", status: "warning", text: "AI layers are split" };
    }
    return { label: "Intel layers", status: "neutral", text: "AI layers neutral" };
  }
  // No stance at all (even derived) — truly no intel layers
  return { label: "Intel layers", status: "neutral", text: "No intel alignment data" };
}

export interface DirectionSummary {
  up: number;
  down: number;
  flat: number;
  unknown: number;
}

export interface ForecastLayerSummaryRow {
  label: string;
  value: string;
  direction: "up" | "down" | "flat" | "unknown";
  weight: number;
  countsInVote: boolean;
}

export interface ForecastDirectionSummary {
  action: "BULLISH" | "BEARISH" | "MIXED" | "FLAT";
  confidence: number;
  reason: string;
  counts: DirectionSummary;
  totalDirectionalVotes: number;
  layers: ForecastLayerSummaryRow[];
}

function readableDirectionValue(value?: string): string | undefined {
  if (!value) return undefined;
  return value.replace(/_/g, " ");
}

function summarizeForecastDirection(data: IntelData): ForecastDirectionSummary {
  const summary: DirectionSummary = { up: 0, down: 0, flat: 0, unknown: 0 };
  const layers: ForecastLayerSummaryRow[] = [];
  const add = (label: string, dir?: string, weight = 1) => {
    const direction = directionLabel(dir);
    summary[direction] += weight;
    if (!dir) return;
    layers.push({ label, value: readableDirectionValue(dir) ?? dir, direction, weight, countsInVote: direction !== "unknown" });
  };

  add("HMM Daily", data.hmm_regime);
  add("HMM 4H", data.hmm_4h_regime);
  add("HMM Hourly", data.hmm_hourly_regime);
  if (data.kronos_daily_valid !== false) add("Kronos Daily", data.kronos_direction);
  if (data.kronos_4h_valid !== false) add("Kronos 4H", data.kronos_4h_direction);
  if (data.kronos_hourly_valid !== false) add("Kronos Hourly", data.kronos_hourly_direction);

  const stance = (data.intel_stance || "").toLowerCase();
  if (stance.includes("aligned_bullish")) add("Intel Stance", "up", 2);
  else if (stance.includes("aligned_bearish")) add("Intel Stance", "down", 2);
  else if (data.intel_stance) layers.push({ label: "Intel Stance", value: readableDirectionValue(data.intel_stance) ?? data.intel_stance, direction: directionLabel(data.intel_stance), weight: 0, countsInVote: false });

  const directionalTotal = summary.up + summary.down + summary.flat;
  const resultBase = { counts: summary, totalDirectionalVotes: directionalTotal, layers };
  if (directionalTotal === 0) {
    return { ...resultBase, action: "MIXED", confidence: 0, reason: "No clear rolling model forecast yet" };
  }

  const confidenceFor = (count: number) => Math.round((count / directionalTotal) * 100);
  const strongestDirectionalCount = Math.max(summary.up, summary.down);
  const strongestDirectionalConfidence = confidenceFor(strongestDirectionalCount);
  const hasEnoughModelDepth = directionalTotal >= 4;

  // Forecast labels are market-analysis labels, not executable trade actions.
  // Use a dominant majority across HMM/Kronos/stance before calling it bullish/bearish;
  // otherwise show MIXED so we don't imply a clean edge from a thin or conflicted stack.
  if (hasEnoughModelDepth && summary.up > summary.down && strongestDirectionalConfidence >= 65) {
    return { ...resultBase, action: "BULLISH", confidence: strongestDirectionalConfidence, reason: "Rolling model forecast leans bullish" };
  }
  if (hasEnoughModelDepth && summary.down > summary.up && strongestDirectionalConfidence >= 65) {
    return { ...resultBase, action: "BEARISH", confidence: strongestDirectionalConfidence, reason: "Rolling model forecast leans bearish" };
  }
  if (summary.flat > strongestDirectionalCount && confidenceFor(summary.flat) >= 50) {
    return { ...resultBase, action: "FLAT", confidence: confidenceFor(summary.flat), reason: "Rolling model forecast is mostly sideways/ranging" };
  }

  return {
    ...resultBase,
    action: "MIXED",
    confidence: confidenceFor(Math.max(summary.up, summary.down, summary.flat)),
    reason: "Rolling model inputs conflict",
  };
}

export function summarizeLiveForecastDirection(data: IntelData): ForecastDirectionSummary {
  return summarizeForecastDirection(data);
}

export function getVerdict(data: IntelData): InstrumentVerdict {
  const checks: CheckItem[] = [];

  // Pending session — no signals yet
  if (data.pending_session) {
    const label = data.pending_session === "asia" ? "Asia" : "US";
    return {
      action: "WAIT",
      kind: "empty",
      confidence: 0,
      reason: `${label} session hasn't happened yet`,
      checks: [],
      riskLevel: "LOW",
    };
  }

  // Explicit missing state — do not make absent Convex data look like a neutral live read.
  if (data.data_status === "missing") {
    return {
      action: "WAIT",
      kind: "empty",
      confidence: 0,
      reason: "No live Convex intel for this symbol",
      checks: [],
      riskLevel: "LOW",
    };
  }

  // Stale live data must never render as an actionable market/trade call.
  // Command Center can still show the stale row for visibility, but the verdict
  // is WAIT until the price/intel feed is fresh again.
  const staleReasons = [
    data.data_status === "stale" ? "dashboard snapshot" : null,
    data.current_price_status === "stale" ? "current price" : null,
    data.current_price_status === "missing" ? "current price missing" : null,
  ].filter(Boolean);
  if (staleReasons.length > 0) {
    return {
      action: "WAIT",
      kind: "empty",
      confidence: 0,
      reason: `Waiting for fresh data (${staleReasons.join(", ")})`,
      checks: [],
      riskLevel: "LOW",
    };
  }

  // Determine live market lean or executable signal direction.
  const hasSignal = !!data.direction;
  const signalDir = directionLabel(data.direction);
  const isLiveMarketRead = isLiveSnapshotAgent(data.agent);
  const comparisonLabel = isLiveMarketRead ? "the market lean" : "your signal";

  // Build checklist from all intel layers — compare against the live lean or executable signal.
  const hmm = translateHMM(data.hmm_regime, data.hmm_probability, signalDir, comparisonLabel);
  const kronos = translateKronos(data.kronos_direction, data.kronos_confidence, data.kronos_daily_change_pct, signalDir, comparisonLabel);
  // Session forecast: use RESOLVED values from dataResolution.ts.
  // Fallback logic (4H confidence < 30% → hourly) is handled in resolveIntelData().
  // translate.ts NEVER makes fallback or threshold decisions.
  const resolvedData = data as import("./dataResolution").ResolvedIntel;
  const kronos4h = translateKronos4H(
    resolvedData._session_forecast_dir,
    resolvedData._session_forecast_conf,
    signalDir,
    comparisonLabel,
  );
  const adx = translateADX(data.adx, data.adx_label, data.adx_plus_di, data.adx_minus_di, signalDir, isLiveMarketRead);
  const squeeze = translateSqueeze(data.squeeze_on, data.squeeze_fired, data.squeeze_momentum);
  const vix = translateVIX(data.vix);
  const stance = translateStance(data.intel_stance, data.intel_alignment_score, signalDir, comparisonLabel);

  checks.push(hmm, kronos, kronos4h, adx, squeeze, vix, stance);

  // Missing direction: live rows can still show a forecast summary from model layers;
  // executable strategy rows without direction remain non-actionable.
  if (!hasSignal || signalDir === "unknown") {
    if (isLiveMarketRead) {
      const forecast = summarizeForecastDirection(data);
      return {
        action: forecast.action,
        kind: "forecast",
        confidence: forecast.confidence,
        reason: forecast.reason,
        checks,
        riskLevel: "LOW",
      };
    }

    const hasAnyIntel = checks.some(c => c.status !== "neutral");
    let waitReason = "No signal for this session";
    if (hasAnyIntel) {
      const agree = checks.filter(c => c.status === "agree").length;
      const warn = checks.filter(c => c.status === "warning").length;
      if (agree > warn) waitReason = "AI models lean bullish — waiting for signal";
      else if (warn > agree) waitReason = "AI models see mixed signals — stay out";
      else waitReason = "AI models neutral — no edge right now";
    }
    return {
      action: "WAIT",
      kind: "empty",
      confidence: 0,
      reason: waitReason,
      checks,
      riskLevel: "LOW",
    };
  }

  // ── Count intel agreement with signal direction ──
  // All 6 directional layers: HMM (daily/4H/hourly) + Kronos (daily/4H/hourly)
  const directionalLayers = [
    { dir: directionLabel(data.hmm_regime), name: "HMM Daily" },
    { dir: directionLabel(data.hmm_4h_regime), name: "HMM 4H" },
    { dir: directionLabel(data.hmm_hourly_regime), name: "HMM Hourly" },
    { dir: directionLabel(data.kronos_direction), name: "Kronos Daily" },
    { dir: directionLabel(data.kronos_4h_direction), name: "Kronos 4H" },
    { dir: directionLabel(data.kronos_hourly_direction), name: "Kronos Hourly" },
  ];

  let agreeCount = 0;
  let disagreeCount = 0;
  let warningCount = 0;

  for (const layer of directionalLayers) {
    if (layer.dir === "unknown") { warningCount++; continue; }
    // "flat" (ranging) against a directional signal = soft disagree
    // (model says no trend, so betting on direction is risky)
    if (layer.dir === "flat") {
      disagreeCount += 1;
      
      continue;
    }
    if (layer.dir === signalDir) {
      agreeCount++;
    } else {
      disagreeCount++;
    }
  }

  // ADX is a chop/strength indicator — NOT a directional vote.
  // It affects the checklist display (trending → breakouts work) but
  // does NOT count as agree/disagree with signal direction.
  // Low ADX = choppy → breakouts fail (reflected in Strategy Focus, not verdict votes).

  // Stance
  const stanceStr = (data.intel_stance || "").toLowerCase();
  if (stanceStr.includes("aligned_bullish") || stanceStr.includes("aligned_bearish")) {
    const alignedDir = stanceStr.includes("bullish") ? "up" : "down";
    if (alignedDir === signalDir) agreeCount += 2;
    else disagreeCount += 2;
  } else if (stanceStr.includes("conflict") || stanceStr.includes("mixed")) {
    warningCount++;
  }

  // Squeeze fired in signal direction
  if (data.squeeze_fired) {
    const sqDir = data.squeeze_momentum && data.squeeze_momentum > 0 ? "up" : data.squeeze_momentum && data.squeeze_momentum < 0 ? "down" : "unknown";
    if (sqDir === signalDir) agreeCount += 2;
  }

  // ── KEY FIX: Override verdict when intel strongly contradicts signal ──
  // If more layers disagree than agree, flip to WAIT
  const isSignalUp = signalDir === "up";
  const intelOverwhelminglyAgainst = disagreeCount >= 3 && agreeCount <= 1;
  const intelMajorityAgainst = disagreeCount > agreeCount + 1;

  // Calculate confidence (0-100)
  const totalLayers = agreeCount + disagreeCount + warningCount;
  const rawConfidence = totalLayers > 0
    ? Math.round((agreeCount / totalLayers) * 100)
    : 50;

  let confidence = rawConfidence;
  if (data.intel_alignment_score !== undefined && data.intel_alignment_score !== null) {
    confidence = Math.round((rawConfidence + data.intel_alignment_score) / 2);
  }
  confidence = Math.max(0, Math.min(100, confidence));

  // Determine action — intel can override executable signals. Live rows render forecast states.
  let action: VerdictAction;
  let reason: string;

  if (isLiveMarketRead) {
    const forecast = summarizeForecastDirection(data);
    action = forecast.action;
    confidence = forecast.confidence;
    reason = forecast.reason;
  } else if (intelOverwhelminglyAgainst) {
    // Intel strongly contradicts the signal → WAIT, don't take the trade
    action = "WAIT";
    reason = isSignalUp
      ? `Signal says buy but ${disagreeCount} AI layers say down — don't fight the models`
      : `Signal says sell but ${disagreeCount} AI layers say up — don't fight the models`;
  } else if (intelMajorityAgainst) {
    // Intel leans against → show counter direction as SELL/BUY with very low confidence
    action = isSignalUp ? "SELL" : "BUY";
    confidence = Math.min(confidence, 25);
    reason = isSignalUp
      ? `Signal says buy but AI models mostly disagree — consider selling instead`
      : `Signal says sell but AI models mostly disagree — consider buying instead`;
  } else if (agreeCount >= 4 && disagreeCount <= 1) {
    action = isSignalUp ? "BUY" : "SELL";
    reason = isSignalUp
      ? "All systems say UP — strong buy signal"
      : "All systems say DOWN — strong sell signal";
  } else if (agreeCount >= 2 && disagreeCount <= 1) {
    action = isSignalUp ? "BUY" : "SELL";
    reason = isSignalUp
      ? "Mostly bullish — good buy signal"
      : "Mostly bearish — good sell signal";
  } else if (disagreeCount >= 3) {
    action = "WAIT";
    reason = "Mixed signals — be careful";
  } else {
    action = isSignalUp ? "BUY" : "SELL";
    reason = isSignalUp
      ? "Market says up but some disagree"
      : "Market says down but some disagree";
  }

  // Risk level
  let riskLevel: "LOW" | "MEDIUM" | "HIGH";
  if (action === "WAIT") riskLevel = "LOW";
  else if (confidence >= 70 && disagreeCount <= 1) riskLevel = "LOW";
  else if (confidence >= 40) riskLevel = "MEDIUM";
  else riskLevel = "HIGH";

  return {
    action,
    kind: isLiveMarketRead ? "forecast" : "trade",
    confidence,
    reason,
    checks,
    riskLevel,
  };
}


// ── Symbol info ──

export const SYMBOL_INFO: Record<string, { name: string; shortName: string }> = {
  ES: { name: "S&P 500", shortName: "S&P" },
  NQ: { name: "Nasdaq 100", shortName: "Nasdaq" },
  GC: { name: "Gold", shortName: "Gold" },
  CL: { name: "Crude Oil", shortName: "Oil" },
  HG: { name: "Copper", shortName: "Copper" },
};

// ── Time helpers ──

export function relativeTime(ts?: number): string {
  if (!ts || !Number.isFinite(ts)) return "";
  const delta = Date.now() - ts;
  if (delta < 60000) return "just now";
  if (delta < 3600000) return `${Math.round(delta / 60000)}m ago`;
  if (delta < 86400000) return `${Math.round(delta / 3600000)}h ago`;
  return `${Math.round(delta / 86400000)}d ago`;
}

export function formatDollars(amount: number): string {
  const sign = amount >= 0 ? "+" : "-";
  return `${sign}$${Math.abs(Math.round(amount)).toLocaleString()}`;
}

export function confidenceLabel(confidence: number): string {
  if (confidence >= 70) return "High confidence";
  if (confidence >= 40) return "Medium confidence";
  return "Low confidence";
}

// ── Strategy Focus ──

export interface StrategyFocus {
  overallRegime: "TRENDING UP" | "TRENDING DOWN" | "RANGING" | "LOW VOL" | "MIXED";
  overallEmoji: string;
  instruments: InstrumentStrategy[];
}

export interface InstrumentStrategy {
  symbol: string;
  regime: "TRENDING UP" | "TRENDING DOWN" | "RANGING" | "LOW VOL" | "MIXED";
  regimeEmoji: string;
  strategies: StrategyRec[];
}

export interface StrategyRec {
  name: string; // ORB Long, ORB Short, FBD, MR
  action: "FOCUS" | "OK" | "SKIP";
  reason: string;
}

function getRegimeForSymbol(d: IntelData | undefined): { regime: InstrumentStrategy["regime"]; emoji: string } {
  if (!d) return { regime: "MIXED", emoji: "🔄" };

  const hmm = (d.hmm_regime || "").toLowerCase();
  const kronos = (d.kronos_direction || "").toLowerCase();
  const adx = d.adx;

  let upScore = 0, downScore = 0, rangeScore = 0, lowVolScore = 0;

  if (hmm.includes("trending_up")) upScore += 3;
  else if (hmm.includes("trending_down")) downScore += 3;
  else if (hmm.includes("low_vol")) lowVolScore += 3;
  else if (hmm.includes("ranging")) rangeScore += 3;

  if (kronos === "up") upScore += 2;
  else if (kronos === "down") downScore += 2;

  // ADX = chop index. High = trending (supports direction), Low = choppy (ranging)
  if (adx !== undefined && adx !== null) {
    if (adx >= 25) {
      // Strong trend — amplify the direction that's already winning
      if (upScore >= downScore) upScore += 1;
      else downScore += 1;
    } else if (adx < 20) {
      // Choppy — ranging regime
      rangeScore += 2;  // Stronger signal: low ADX clearly means range
    }
  }

  const maxScore = Math.max(upScore, downScore, rangeScore, lowVolScore);
  if (maxScore === 0) return { regime: "MIXED", emoji: "🔄" };

  const scores = [upScore, downScore, rangeScore, lowVolScore].sort((a, b) => b - a);
  if ((scores[0] ?? 0) <= (scores[1] ?? 0) * 1.3) return { regime: "MIXED", emoji: "🔄" };

  if (upScore === maxScore) return { regime: "TRENDING UP", emoji: "📈" };
  if (downScore === maxScore) return { regime: "TRENDING DOWN", emoji: "📉" };
  if (lowVolScore === maxScore) return { regime: "LOW VOL", emoji: "😴" };
  return { regime: "RANGING", emoji: "↔️" };
}

function getStrategiesForRegime(regime: InstrumentStrategy["regime"]): StrategyRec[] {
  const strategies: StrategyRec[] = [];

  switch (regime) {
    case "TRENDING UP":
      strategies.push(
        { name: "ORB Long", action: "FOCUS", reason: "Breakouts in an uptrend" },
        { name: "FBD", action: "FOCUS", reason: "Short traps = buying opportunities" },
        { name: "ORB Short", action: "SKIP", reason: "Fighting the trend" },
        { name: "MR", action: "SKIP", reason: "Shorts get run over" },
      );
      break;

    case "TRENDING DOWN":
      strategies.push(
        { name: "ORB Short", action: "FOCUS", reason: "Breakdowns in a downtrend" },
        { name: "MR", action: "FOCUS", reason: "Fade overextended rallies" },
        { name: "FBD", action: "SKIP", reason: "Long traps get bulldozed" },
        { name: "ORB Long", action: "SKIP", reason: "Longs in a downtrend" },
      );
      break;

    case "RANGING":
      strategies.push(
        { name: "MR", action: "FOCUS", reason: "Fade extremes in a range" },
        { name: "FBD", action: "OK", reason: "Quick traps, be selective" },
        { name: "ORB Long", action: "OK", reason: "Breakouts may fail" },
        { name: "ORB Short", action: "OK", reason: "Breakdowns may fail" },
      );
      break;

    case "LOW VOL":
      strategies.push(
        { name: "FBD", action: "OK", reason: "Tight range traps" },
        { name: "MR", action: "OK", reason: "Small fades" },
        { name: "ORB Long", action: "SKIP", reason: "Weak breakouts" },
        { name: "ORB Short", action: "SKIP", reason: "Weak breakdowns" },
      );
      break;

    case "MIXED":
    default:
      strategies.push(
        { name: "ORB", action: "OK", reason: "Signals disagree — high conviction only" },
        { name: "MR", action: "OK", reason: "Fade extremes, small size" },
        { name: "FBD", action: "OK", reason: "Selective traps only" },
      );
      break;
  }

  const order: Record<string, number> = { FOCUS: 0, OK: 1, SKIP: 2 };
  strategies.sort((a, b) => (order[a.action] ?? 1) - (order[b.action] ?? 1));
  return strategies;
}

export function getStrategyFocus(intelMap: Record<string, IntelData>): StrategyFocus {
  const symbols = ["ES", "NQ", "GC", "CL", "HG"];

  const instruments: InstrumentStrategy[] = symbols.map(sym => {
    const d = intelMap[sym];
    const { regime, emoji } = getRegimeForSymbol(d);
    return {
      symbol: sym,
      regime,
      regimeEmoji: emoji,
      strategies: getStrategiesForRegime(regime),
    };
  });

  // Overall regime: weighted aggregate (ES/NQ count 2x)
  let upVotes = 0, downVotes = 0, rangeVotes = 0, lowVolVotes = 0;
  for (const inst of instruments) {
    const w = (inst.symbol === "ES" || inst.symbol === "NQ") ? 2 : 1;
    if (inst.regime === "TRENDING UP") upVotes += w;
    else if (inst.regime === "TRENDING DOWN") downVotes += w;
    else if (inst.regime === "RANGING") rangeVotes += w;
    else if (inst.regime === "LOW VOL") lowVolVotes += w;
  }

  const maxV = Math.max(upVotes, downVotes, rangeVotes, lowVolVotes);
  let overallRegime: StrategyFocus["overallRegime"];
  let overallEmoji: string;

  if (maxV === 0) { overallRegime = "MIXED"; overallEmoji = "🔄"; }
  else if (upVotes === maxV && upVotes > downVotes * 1.3) { overallRegime = "TRENDING UP"; overallEmoji = "📈"; }
  else if (downVotes === maxV && downVotes > upVotes * 1.3) { overallRegime = "TRENDING DOWN"; overallEmoji = "📉"; }
  else if (lowVolVotes === maxV) { overallRegime = "LOW VOL"; overallEmoji = "😴"; }
  else if (rangeVotes >= upVotes && rangeVotes >= downVotes) { overallRegime = "RANGING"; overallEmoji = "↔️"; }
  else { overallRegime = "MIXED"; overallEmoji = "🔄"; }

  return { overallRegime, overallEmoji, instruments };
}
